import { z } from 'zod';
import { LangCodeSchema, TimestampSchema, UuidSchema } from './primitives';

// =============================================================================
// Tenant settings
// =============================================================================

/** Address sub-object stored as jsonb in `tenant_settings.address`. */
export const AddressSchema = z
  .object({
    line1: z.string().max(200).optional(),
    line2: z.string().max(200).optional(),
    city: z.string().max(100).optional(),
    state: z.string().max(100).optional(),
    postal_code: z.string().max(20).optional(),
    country: z.string().length(2).optional(), // ISO 3166-1 alpha-2
  })
  .catchall(z.unknown());
export type Address = z.infer<typeof AddressSchema>;

/** Contact sub-object stored as jsonb in `tenant_settings.contact`. */
export const ContactSchema = z
  .object({
    email: z.string().email().optional(),
    phone: z.string().max(40).optional(),
    website: z.string().url().optional(),
  })
  .catchall(z.unknown());
export type Contact = z.infer<typeof ContactSchema>;

/** Full `tenant_settings` row returned by GET /settings/tenant. */
export const TenantSettingsSchema = z.object({
  tenant_id: UuidSchema,
  display_name: z.string().min(1).max(200),
  legal_name: z.string().max(300).nullable(),
  logo_url: z.string().nullable(), // Storage key (not a full URL until resolved)
  primary_locale: LangCodeSchema,
  default_timezone: z.string().min(1).max(64),
  address: AddressSchema,
  contact: ContactSchema,
  translation_default_on: z.boolean(),
  created_at: TimestampSchema,
  updated_at: TimestampSchema,
});
export type TenantSettings = z.infer<typeof TenantSettingsSchema>;

/** Input schema for PATCH /settings/tenant. All fields optional (partial update). */
export const TenantSettingsUpdateInputSchema = z
  .object({
    display_name: z.string().min(1).max(200),
    legal_name: z.string().max(300).nullable(),
    logo_url: z.string().nullable(),
    primary_locale: LangCodeSchema,
    default_timezone: z.string().min(1).max(64),
    address: AddressSchema,
    contact: ContactSchema,
    translation_default_on: z.boolean(),
  })
  .partial();
export type TenantSettingsUpdateInput = z.infer<typeof TenantSettingsUpdateInputSchema>;

// =============================================================================
// Logo upload
// =============================================================================

/** Shared response shape for signed upload URL issuance. */
export const SignedUploadResponseSchema = z.object({
  upload_url: z.string().url(),
  storage_key: z.string(),
  expires_at: z.string().datetime({ offset: true }),
  max_bytes: z.number().int().positive(),
});
export type SignedUploadResponse = z.infer<typeof SignedUploadResponseSchema>;

/** Response from POST /settings/tenant/logo (signed upload URL). */
export const LogoUploadResponseSchema = SignedUploadResponseSchema;
export type LogoUploadResponse = z.infer<typeof LogoUploadResponseSchema>;

/** Response from POST /settings/me/avatar (signed upload URL + public URL). */
export const AvatarUploadResponseSchema = SignedUploadResponseSchema.extend({
  public_url: z.string().url(),
});
export type AvatarUploadResponse = z.infer<typeof AvatarUploadResponseSchema>;

// =============================================================================
// "Me" composite view
// =============================================================================

/** Response from GET /settings/me — user profile merged with tenant membership. */
export const MeResponseSchema = z.object({
  user: z.object({
    id: UuidSchema,
    email: z.string().email(),
    display_name: z.string().nullable(),
    avatar_url: z.string().nullable(),
    phone: z.string().nullable(),
    timezone: z.string(),
    preferred_lang: LangCodeSchema,
    theme: z.enum(['system', 'light', 'dark']),
  }),
  membership: z.object({
    tenant_id: UuidSchema,
    role_name: z.string(),
    joined_at: TimestampSchema,
  }),
  membership_profile: z
    .object({
      title: z.string().nullable(),
      department: z.string().nullable(),
      bio: z.string().nullable(),
      status: z.enum(['active', 'invited', 'suspended', 'terminated']),
    })
    .nullable(),
});
export type MeResponse = z.infer<typeof MeResponseSchema>;
